import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';
import { balanceUpdateAPI } from '../services/api';
import {
  Upload, ArrowLeft, Loader, FileSpreadsheet, RefreshCw, CheckCircle2, Download,
} from 'lucide-react';
import useDarkReady from '../theme/useDarkReady';

const TEMPLATE_COLUMNS = ['Account Number', 'Name', 'Address', 'Mobile', 'Combo Linked', 'Current Outstanding', 'New Outstanding'];

const fmt = n => new Intl.NumberFormat('en-IN').format(Math.round(n || 0));

function parseSheetFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });
        if (rows.length === 0) return reject(new Error('No data rows found in the file'));
        resolve(rows);
      } catch (err) {
        reject(new Error(`Cannot parse file: ${err.message}`));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });
}

// Row from a parsed file -> the shape /validate and /apply expect. Column names must match
// backend/src/routes/balanceUpdate.js's parseRow() exactly.
function toRecord(row) {
  return {
    accountNumber: row['Account Number'] ?? row.accountNumber ?? '',
    newOutstanding: row['New Outstanding'] ?? row.newOutstanding ?? '',
  };
}

export default function BalanceUpdatePage() {
  useDarkReady();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [status, setStatus] = useState('active');
  const [durationType, setDurationType] = useState('all');
  const [downloading, setDownloading] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [records, setRecords] = useState(null);

  const [checking, setChecking] = useState(false);
  const [preview, setPreview] = useState(null);

  const [applying, setApplying] = useState(false);
  const [results, setResults] = useState(null);

  const downloadTemplate = async () => {
    setDownloading(true);
    try {
      const { data } = await balanceUpdateAPI.export({ status, durationType });
      const customers = data.data || [];
      const sheetRows = customers.map(c => ({
        'Account Number': c.accountNumber,
        Name: c.name,
        Address: c.address,
        Mobile: c.mobile,
        'Combo Linked': c.comboLinked,
        'Current Outstanding': c.currentOutstanding,
        'New Outstanding': '',
      }));
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(sheetRows, { header: TEMPLATE_COLUMNS });
      XLSX.utils.book_append_sheet(wb, ws, 'Balances');
      XLSX.writeFile(wb, 'catv-balance-update.xlsx');
      toast.success(`Downloaded ${customers.length} customers — fill in "New Outstanding" for the ones you want to change`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  const handleFile = async (file) => {
    setParsing(true);
    setPreview(null);
    setResults(null);
    setRecords(null);
    try {
      const rows = await parseSheetFile(file);
      setRecords(rows);
      toast.success(`Parsed ${rows.length} rows — click "Check File" to preview the changes`);
    } catch (err) {
      toast.error(err.message || 'Could not parse file');
    } finally {
      setParsing(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const runValidate = async () => {
    if (!records) return;
    setChecking(true);
    try {
      const { data } = await balanceUpdateAPI.validate({ records: records.map(toRecord) });
      setPreview(data.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Check failed');
    } finally {
      setChecking(false);
    }
  };

  const runApply = async () => {
    if (!records || !preview) return;
    if (!window.confirm(
      `Update ${preview.matched} customer${preview.matched === 1 ? '' : 's'}' outstanding balance now?\n\n` +
      'Every change is logged in System Logs and a backup file is saved on the server before applying.'
    )) return;

    setApplying(true);
    try {
      const { data } = await balanceUpdateAPI.apply({ records: records.map(toRecord) });
      setResults(data.data);
      toast.success(`Updated ${data.data.updated} customer${data.data.updated === 1 ? '' : 's'}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
    } finally {
      setApplying(false);
    }
  };

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      <div className="flex items-center gap-3 mb-6">
        <button className="btn btn-secondary btn-sm" onClick={() => navigate('/customers')}>
          <ArrowLeft size={14} />
        </button>
        <div>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700, margin: 0 }}>Balance Update</h2>
          <p className="text-muted text-small" style={{ margin: 0 }}>
            Download every CATV customer's current outstanding balance, fill in the correct amount for the ones that are wrong, then re-upload to apply — matched by Account Number.
          </p>
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-header"><h3 style={{ margin: 0 }}><Download size={15} style={{ verticalAlign: 'middle', marginRight: 6, color: 'var(--primary)' }} />Step 1 — Download</h3></div>
        <div className="card-body">
          <div className="flex items-center gap-2 mb-3">
            <label className="text-small text-muted" htmlFor="balance-status-filter">Customer status:</label>
            <select
              id="balance-status-filter"
              className="input"
              style={{ width: 'auto' }}
              value={status}
              onChange={e => setStatus(e.target.value)}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="suspended">Suspended</option>
              <option value="all">All</option>
            </select>
            <label className="text-small text-muted" htmlFor="balance-duration-filter">Billing type:</label>
            <select
              id="balance-duration-filter"
              className="input"
              style={{ width: 'auto' }}
              value={durationType}
              onChange={e => setDurationType(e.target.value)}
            >
              <option value="all">All</option>
              <option value="regular">Regular (Monthly)</option>
              <option value="dtd">DTD (Days)</option>
            </select>
          </div>
          <button className="btn btn-primary" disabled={downloading} onClick={downloadTemplate}>
            {downloading ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Download size={14} />}
            {downloading ? 'Preparing…' : 'Download Current Balances (.xlsx)'}
          </button>
          <p className="text-muted text-small" style={{ marginTop: 8 }}>
            Columns: Account Number, Name, Address, Mobile, Combo Linked, Current Outstanding, New Outstanding.
            Leave "New Outstanding" blank for any customer you don't want to change.
          </p>
        </div>
      </div>

      <div className="card">
        <div className="card-header"><h3 style={{ margin: 0 }}><FileSpreadsheet size={15} style={{ verticalAlign: 'middle', marginRight: 6, color: 'var(--primary)' }} />Step 2 — Upload the edited file</h3></div>
        <div className="card-body">
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            style={{ display: 'none' }}
            onChange={e => e.target.files[0] && handleFile(e.target.files[0])}
          />
          <button className="btn btn-primary" disabled={parsing} onClick={() => fileRef.current?.click()}>
            {parsing ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Upload size={14} />}
            {parsing ? 'Parsing…' : 'Choose File'}
          </button>

          {records && (
            <div style={{ marginTop: 16 }}>
              <p style={{ fontSize: '0.85rem', margin: '0 0 12px' }}>{records.length} rows parsed.</p>
              <button className="btn btn-secondary btn-sm" disabled={checking} onClick={runValidate}>
                {checking ? <Loader size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <RefreshCw size={13} />}
                {checking ? 'Checking…' : 'Check File'}
              </button>

              {preview && (
                <div style={{ marginTop: 16 }}>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
                    <SummaryTile label="Will update" value={preview.matched} color="var(--c-065f46)" bg="var(--cb-d1fae5)" />
                    <SummaryTile label="Already matches — no change" value={preview.unchanged} color="var(--c-374151)" bg="var(--cb-e5e7eb)" />
                    <SummaryTile label="Account not found" value={preview.notFound} color="var(--c-991b1b)" bg="var(--cb-fee2e2)" />
                    <SummaryTile label="Left blank" value={preview.blank} color="var(--c-6b7280)" bg="var(--cb-f3f4f6)" />
                  </div>

                  {preview.rows.length > 0 && (
                    <div style={{ overflowX: 'auto', marginBottom: 14 }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                        <thead>
                          <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
                            <th>Account No.</th><th>Name</th>
                            <th style={{ textAlign: 'right' }}>Current</th>
                            <th style={{ textAlign: 'right' }}>New</th>
                            <th style={{ textAlign: 'right' }}>Diff</th>
                          </tr>
                        </thead>
                        <tbody>
                          {preview.rows.map((r, i) => (
                            <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                              <td>{r.accountNumber}</td>
                              <td>{r.found ? r.name : <span style={{ color: 'var(--c-991b1b)' }}>Not found</span>}</td>
                              <td style={{ textAlign: 'right' }}>{r.found ? `₹${fmt(r.oldBalance)}` : '—'}</td>
                              <td style={{ textAlign: 'right' }}>{r.found ? `₹${fmt(r.newBalance)}` : '—'}</td>
                              <td style={{ textAlign: 'right', color: r.diff > 0 ? 'var(--c-991b1b)' : r.diff < 0 ? 'var(--c-065f46)' : undefined }}>
                                {r.found ? `${r.diff >= 0 ? '+' : ''}₹${fmt(r.diff)}` : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <button className="btn btn-primary btn-sm" disabled={applying || preview.matched === 0} onClick={runApply}>
                    {applying ? <Loader size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <CheckCircle2 size={13} />}
                    {applying ? 'Applying…' : `Apply Update (${preview.matched})`}
                  </button>
                </div>
              )}

              {results && (
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 14 }}>
                  <SummaryTile label="Updated" value={results.updated} color="var(--c-065f46)" bg="var(--cb-d1fae5)" />
                  <SummaryTile label="Not found" value={results.notFound} color="var(--c-991b1b)" bg="var(--cb-fee2e2)" />
                  <SummaryTile label="Skipped (no change)" value={results.skipped} color="var(--c-374151)" bg="var(--cb-e5e7eb)" />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SummaryTile({ label, value, color, bg }) {
  return (
    <div style={{ background: bg, color, borderRadius: 8, padding: '10px 16px', minWidth: 140 }}>
      <div style={{ fontSize: '1.3rem', fontWeight: 700 }}>{value}</div>
      <div style={{ fontSize: '0.75rem' }}>{label}</div>
    </div>
  );
}
